import { containerExtension } from "@cuekards/core/recording";
import type { TakeOutputPorts } from "@cuekards/core/take-output";

/**
 * Handing the finished take over — the web's answer to the desktop's remux, and
 * deliberately not one.
 *
 * On the desktop, turning a capture into the file the user keeps is a conversion:
 * ffmpeg runs, the video is copied or re-encoded, and an MP4 lands in a fixed
 * folder. A tab can do none of that. **In-browser transcoding is rejected**, and
 * with reasons rather than by omission: a ten-minute high-definition take is
 * close to the address-space ceiling of a WebAssembly transcoder even for a
 * stream copy, and the failure mode would be running out of memory at the exact
 * moment an unrepeatable take is being saved.
 *
 * So the web's step in the same place is a *handover*. The bytes that were
 * captured are the bytes the user gets, and where they go is the user's choice —
 * which means a save dialog, which means the page, because that is a window API.
 * The bytes are in origin-private storage, which both halves can reach, so what
 * crosses is the path and the name rather than the media.
 */

/** What the worker asks the page to deliver. */
export interface DeliveryRequest {
  /** Where the capture sits in origin-private storage, slash-separated. */
  path: string;
  /** What to call the file — derived from the moment the take was shot and the
   * document's title (FR-48), with the extension of what was actually captured. */
  fileName: string;
  /** The container the recorder settled on, which decides whether there is
   * anything to explain. */
  mimeType: string;
}

/** What came of it. Shaped like `RemuxResult` because it becomes one. */
export type DeliveryResult =
  | { ok: true; output: string }
  | { ok: false; message: string };

/**
 * The web's {@link TakeOutputPorts}: the naming rules stay shared, and the four
 * questions about a filesystem are answered by a host that does not have one.
 *
 * Sharing the naming is the point of filling the port at all rather than writing
 * a second path. A take recovered after a crash (web ticket 13) is the same take
 * and has to come out under the same name by the same rules, and two copies of
 * that arithmetic would eventually disagree — by overwriting footage.
 *
 * - **There is no output folder**, so there is nothing to ensure and nothing to
 *   join onto: a browser file has a name and the user chooses where it lands.
 * - **Nothing can be looked up**, so no file "exists" — the save dialog and the
 *   downloads folder each deal with a collision themselves, and asking a browser
 *   what is on the user's disk is not a thing a page may do.
 * - **The extension follows the capture**, because this host cannot convert
 *   anything: see {@link containerExplanation}.
 */
export function webTakeOutput(
  deliver: (request: DeliveryRequest) => Promise<DeliveryResult>,
): TakeOutputPorts {
  return {
    // Named `remux` by the port and deliberately not one — see the note at the
    // top of this file. Nothing is converted; the capture is handed over.
    remux: ({ input, output, mimeType }) =>
      deliver({ path: input, fileName: output, mimeType }),
    ensureOutputDirectory: () => "",
    outputPath: (_directory, fileName) => fileName,
    fileExists: () => false,
    outputExtension: containerExtension,
  };
}

/**
 * What to tell the user about the container their take is in, or undefined when
 * there is nothing to say.
 *
 * Every Chromium measured for web ticket 01 records MP4 directly, so this is the
 * fallback rather than the expected path. It exists because the fallback has to
 * be *honest*: the file plays, it is the footage, and some editors will refuse
 * it — and the reason nothing was done about that is a deliberate decision
 * rather than a missing feature, so it says so.
 */
export function containerExplanation(mimeType: string): string | undefined {
  const extension = containerExtension(mimeType);
  if (extension === "mp4") return undefined;
  const name = extension === "mkv" ? "Matroska" : "WebM";
  return (
    `This browser couldn't record MP4, so the take was saved as ${name} — ` +
    `it is the footage exactly as it was captured and it plays, but some ` +
    `editors will not take it. Converting it here would risk running out of ` +
    `memory on a long take, so nothing was converted. Open it in your editor ` +
    `first; if it is refused, convert the file on your machine, or use a ` +
    `recent Chrome or Edge, which record MP4 directly.`
  );
}
